import { logger } from 'v2/utils/logger'
import { createGlobalHeaders, getActiveRegion } from '../shared/services/create-service'
import { embedDpopHeaders } from '../shared/services/service-interceptors'
import {
  dispatchEmbedSessionExpired,
  getEmbedSession,
  isEmbedMode,
  toEmbedPath,
} from '../shared/utils/embed-session'

export interface StreamHandlers<TData> {
  onMessage: (data: TData) => void
  onError: (error: unknown) => void
  onEvent?: (event: string | undefined, data: TData) => void
  onDone?: () => void
}

export type StreamFunction = <TBody, TData>(
  path: string,
  body: TBody,
  handlers: StreamHandlers<TData>,
) => AbortController

/**
 * Creates an SSE stream connection with automatic parsing and error handling
 * @param baseURL - The base URL for the service
 * @param path - The endpoint path to stream from
 * @param body - The request body to send
 * @param handlers - Event handlers for messages, errors, and completion
 * @returns AbortController to control the stream
 */
export const createSSEStream = <TBody, TData>(
  baseURL: string,
  path: string,
  body: TBody,
  handlers: StreamHandlers<TData>,
): AbortController => {
  const controller = new AbortController()
  let serviceBaseURL = baseURL?.replace(/\/$/, '') || ''

  // Manually replace {{region}} placeholder since fetch bypasses axios
  // interceptors. In embed mode the region comes from the embed session.
  if (serviceBaseURL.includes('{{region}}')) {
    const region = getActiveRegion()
    if (!region) {
      handlers.onError(new Error('Region is not set, cannot make request'))
      handlers.onDone?.()
      return controller
    }
    serviceBaseURL = serviceBaseURL.replace('{{region}}', region)
  }

  // Embed mode: this fetch bypasses the axios interceptors entirely, so the
  // /v1/ → /v1/embed/ rewrite must happen here too (the copilot stream is the
  // main caller). toEmbedPath self-gates on embed mode — identity elsewhere.
  // createGlobalHeaders already branches to the embed headers.
  const url = toEmbedPath(`${serviceBaseURL}${path}`)
  // The DPoP proof is async (crypto.subtle.sign), but this function MUST keep
  // returning its AbortController synchronously — callers store it and abort on
  // unmount, and handing back a promise would break every one of them. So the
  // proof is awaited inside a .then() and the fetch is issued from there; the
  // controller is already wired to it via `signal`, so an abort that lands
  // before the fetch starts still cancels it.
  //
  // Outside embed mode this resolves to {} immediately and the behaviour is
  // unchanged apart from a microtask.
  embedDpopHeaders('POST', url, isEmbedMode() ? getEmbedSession() : null)
    .then((proof) =>
      fetch(url, {
        method: 'POST',
        headers: {
          ...createGlobalHeaders(),
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...proof,
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      }),
    )
    .then(async (res) => {
      if (!res.ok || !res.body) {
        // This fetch bypasses the axios interceptors, so an embed-session 401
        // here would otherwise skip the expiry overlay + host re-mint that
        // handleEmbedUnauthorized drives on the axios path. Mirror it.
        if (res.status === 401 && isEmbedMode()) {
          dispatchEmbedSessionExpired()
        }
        try {
          const text = await res.text()
          handlers.onError(new Error(`HTTP ${res.status}: ${text}`))
        } catch (error) {
          handlers.onError(error)
        }
        handlers.onDone?.()
        return
      }
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      try {
        while (true) {
          // Check if the request was aborted before reading
          if (controller.signal.aborted) {
            logger.debug('Stream aborted, breaking read loop')
            break
          }

          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const parts = buffer.split('\n\n')
          buffer = parts.pop() || ''
          parts.forEach((part) => {
            const lines = part.split('\n')
            let event = ''
            let dataLine = ''
            lines.forEach((line) => {
              if (line.startsWith('event:')) event = line.slice(6).trim()
              if (line.startsWith('data:')) dataLine = line.slice(5).trim()
            })
            if (dataLine) {
              try {
                const raw = JSON.parse(dataLine) as unknown
                const data = raw as TData

                if (handlers.onEvent) {
                  handlers.onEvent(event || undefined, data)
                }

                if (event === 'error') {
                  handlers.onError(raw)
                } else {
                  handlers.onMessage(data)
                }
              } catch (e) {
                logger.error('SSE parse error:', e, { dataLine })
                handlers.onError(e as Error)
              }
            }
          })
        }
      } finally {
        // Always close the reader to free up resources
        reader.releaseLock()
      }

      handlers.onDone?.()
    })
    .catch((err: unknown) => {
      if ((err as Error).name === 'AbortError') {
        logger.debug('Stream aborted')
        return
      }
      handlers.onError(err)
      handlers.onDone?.()
    })
  return controller
}
